import Auth, { CognitoUser } from '@aws-amplify/auth';
import {
    ClientMetaData,
    ConfirmSignUpOptions,
    FederatedSignInOptions,
    SignInOpts,
    SignUpParams,
} from '@aws-amplify/auth/lib-esm/types';
import Amplify, { ICredentials } from '@aws-amplify/core';
import { AuthState } from '@aws-amplify/ui-components';
import Config from 'config';
import { createEffect, createStore, sample } from 'effector';

import TermsAndConditionsService from 'services/TermsAndConditionsService';

import { type CognitoUserWithAttributes } from 'types/CognitoUserWithAttributes';
import { type TRole } from 'types/Store';
import { type CurrentParticipant } from 'types/zod/current-participant';

const $authState = createStore<AuthState | null>(null);
const $tempInitialized = createStore<boolean>(false);
const $isAuthenticated = createStore<boolean>(false);
const $currentParticipant = createStore<CurrentParticipant | null>(null);
const $systemRoles = $currentParticipant.map((participant) => participant?.systemRoles || []);

const $initialized = sample({
    source: {
        authState: $authState,
        tempInitialized: $tempInitialized,
        isAuthenticated: $isAuthenticated,
    },
    fn: ({ authState, tempInitialized, isAuthenticated }) =>
        (isAuthenticated && tempInitialized === true) || !authState,
});

const $$auth = {
    $authState,
    $tempInitialized,
    $initialized,
    $currentParticipant,
    $systemRoles,
    $isRelevantSigned: createStore<boolean>(false),
    $user: createStore<CognitoUserWithAttributes | null>(null),
    $isAuthenticated,
    $userRole: createStore<TRole | null>(null),

    initFx: createEffect(() => {
        Auth.configure(Config.amplify);
        Amplify.configure(Config.amplify);
    }),
    resendSignUpFx: createEffect(
        async ({ username, clientMetadata }: { username: string; clientMetadata?: ClientMetaData }) =>
            await Auth.resendSignUp(username, clientMetadata),
    ),
    currentUserPoolUserFx: createEffect(async () => await Auth.currentUserPoolUser()),
    currentAuthenticatedUserFx: createEffect(async () => await Auth.currentAuthenticatedUser()),
    signOutFx: createEffect(async () => await Auth.signOut()),
    signInFx: createEffect(async ({ username, password }: SignInOpts) => await Auth.signIn(username, password, {})),
    federatedSignInFx: createEffect<FederatedSignInOptions, ICredentials>(Auth.federatedSignIn),
    signUpFx: createEffect(async (user: SignUpParams) => await Auth.signUp(user)),
    confirmSignUpFx: createEffect(
        async ({ email, code, options }: { email: string; code: string; options?: ConfirmSignUpOptions }) =>
            await Auth.confirmSignUp(email, code, options),
    ),
    forgotPasswordFx: createEffect(
        async ({ username, clientMetadata }: { username: string; clientMetadata?: ClientMetaData }) =>
            await Auth.forgotPassword(username, clientMetadata),
    ),
    forgotPasswordSubmitFx: createEffect(
        async ({ username, code, password }: { username: string; code: string; password: string }) =>
            await Auth.forgotPasswordSubmit(username, code, password),
    ),
    completeNewPasswordFx: createEffect(
        async ({ user, password }: { user: CognitoUser; password: string }) =>
            await Auth.completeNewPassword(user, password),
    ),
    changePasswordFx: createEffect(
        async ({ user, oldPassword, newPassword }: { user: string; oldPassword: string; newPassword: string }) =>
            await Auth.changePassword(user, oldPassword, newPassword),
    ),
    sendCustomChallengeAnswerFx: createEffect(
        async ({ user, code }: { user: CognitoUser; code: string }) => await Auth.sendCustomChallengeAnswer(user, code),
    ),
    currentSessionFx: createEffect(async () => await Auth.currentSession()),

    checkIsRelevantSignedFx: createEffect(async () => await TermsAndConditionsService.checkIsRelevantSigned('user')),
};

export default $$auth;
